import { useCallback, useEffect, useRef, useState } from "react";

import { ArrowRightIcon, FileIcon, FolderIcon } from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { MicrosoftIcon } from "simplydo/react/ui/brand-icon";
import { Button } from "simplydo/react/ui/button";
import { H4 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Table, TableBody, TableCell, TableRow } from "simplydo/react/ui/table";

import api from "api";

type OneDriveDirectoryEntry = NonNullable<
  OpenAPI.GET<"/uploads/onedrive/directory">["response"]["directory"]
>[number];

type OneDriveFileChooserProps = {
  accept?: string;
  open: boolean;
  forId: string;
  forType: string;
  onComplete: (
    savedName: string,
    fileName: string,
    signedDownloadRequest: string,
  ) => void;
  onError?: (message: string) => void;
  onClose: () => void;
};

export const OneDriveFileChooser = ({
  open,
  accept,
  forId,
  forType,
  onComplete,
  onError,
  onClose,
}: OneDriveFileChooserProps) => {
  const [path, _setPath] = useState("/");
  const [currentDirectory, setCurrentDirectory] = useState<
    OneDriveDirectoryEntry[]
  >([]);
  const [didInit, setDidInit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const fetchRef = useRef("/");

  const setPath = useCallback((newPath: string) => {
    fetchRef.current = newPath;
    setUpdating(true);
    let tempPath = "";
    _setPath((prevPath) => {
      tempPath = prevPath;
      return newPath;
    });
    api.uploads.oneDriveListDirectory(
      newPath,
      (data) => {
        if (fetchRef.current !== newPath) {
          return;
        }
        setUpdating(false);
        setCurrentDirectory(data.directory);
      },
      () => {
        _setPath(tempPath);
        setUpdating(false);
      },
    );
  }, []);

  useEffect(() => {
    if (open && !didInit) {
      setDidInit(true);
      setPath("/");
    }
  }, [didInit, open, setPath]);

  const acceptList = accept?.split(",") ?? [];
  function isChooseable(file: OneDriveDirectoryEntry) {
    if (file.type === "file") {
      if (!accept) return true;

      if (acceptList.includes(file.mimeType)) {
        return true;
      }
    }
    return false;
  }

  function pathComponents() {
    return path.split("/").filter((p) => p.length);
  }

  function setPathToPoint(index: number) {
    let newPath = "/";
    const components = pathComponents();
    for (let i = 0; i <= index; i += 1) {
      const component = components[i];
      if (component) {
        newPath += component;
      }
    }
    setPath(newPath);
  }

  function fileChosen(file: OneDriveDirectoryEntry) {
    if (!file || !isChooseable(file)) return;
    setLoading(true);
    api.uploads.storeOneDriveFileInS3(
      file.itemId,
      file.name,
      forId,
      forType,
      (data) => {
        setLoading(false);
        onComplete(data.fileName, file.name, data.signedDownloadRequest);
      },
      (err) => {
        setLoading(false);
        if (onError) onError(err.message || "Unable to upload file");
      },
    );
  }

  return (
    <Modal
      open={open}
      dimmer="inverted"
      size="small"
      style={{ zIndex: 100 }}
      closeOnDimmerClick={false}
    >
      <ModalHeader>
        <MicrosoftIcon /> Your OneDrive
      </ModalHeader>
      <ModalContent scrolling style={{ minHeight: 250 }}>
        {updating ? (
          <Loader />
        ) : (
          <>
            {path.length > 1 && (
              <div style={{ display: "flex", alignItems: "center" }}>
                <div onClick={() => setPath("/")}>Home</div>
                <Icon icon={ArrowRightIcon} />
                {pathComponents()?.map((p, i) => (
                  <div
                    key={`${p}-${i}`}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <div onClick={() => setPathToPoint(i)}>{p}</div>
                    {i < pathComponents().length - 1 ? (
                      <Icon icon={ArrowRightIcon} />
                    ) : null}
                  </div>
                ))}
              </div>
            )}
            {!updating && !currentDirectory.length && (
              <H4 style={{ textAlign: "center" }}>This directory is empty</H4>
            )}
            <Table basic="very" selectable>
              <TableBody>
                {currentDirectory.map((d) => (
                  <TableRow
                    disabled={d.type === "file" && !isChooseable(d)}
                    key={d.itemId}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      if (d.type === "folder") {
                        setPath(`${path}/${d.name}`);
                      } else {
                        fileChosen(d);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <TableCell collapsing>
                      <Icon
                        icon={d.type === "folder" ? FolderIcon : FileIcon}
                      />
                    </TableCell>
                    <TableCell>{d.name}</TableCell>
                    <TableCell collapsing>
                      {isChooseable(d) ? (
                        <Button
                          loading={loading}
                          size="small"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            fileChosen(d);
                          }}
                          color="primary"
                        >
                          Choose
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </ModalContent>
      <ModalActions>
        <Button onClick={onClose} variant="outline">
          Cancel
        </Button>
      </ModalActions>
    </Modal>
  );
};
